import { CompactColorPickerInput } from "@/overlay/components/LayerStackWindow/LayerFormElements/CompactColorPickerInput";
import { FAB, NumberInput } from "@mm/metx-design-system";
import type { ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import React, { useState } from "react";
import styles from "./Control.module.scss";

type StyleControlsProps = {
  titleStyle: ElementStyle;
  onStyleChange: (newStyle: ElementStyle) => void;
  registerCloseHandler?: (handler: (() => boolean) | null) => void;
};

const TITLE_MIN_SIZE = 8;
const TITLE_MAX_SIZE = 40;

export function TitleStyleControl({ titleStyle, onStyleChange, registerCloseHandler }: StyleControlsProps) {
  const [fontSize, setFontSize] = useState<number>(titleStyle.fontSize || 16);

  const toggleStyle = (styleProp: keyof ElementStyle, activeValue: string | number, normalValue: string | number) => {
    const newStyle = {
      ...titleStyle,
      [styleProp]: titleStyle[styleProp] === activeValue ? normalValue : activeValue, // Toggle the style
    };
    onStyleChange(newStyle);
  };

  const changeColor = (newColor: string | null) => {
    const newStyle = {
      ...titleStyle,
      color: newColor,
    };
    onStyleChange(newStyle);
  };

  const changeFontSize = (size: number) => {
    setFontSize(size);
    onStyleChange({ ...titleStyle, fontSize: size });
  };

  return (
    <div className={styles["style-controls"]} data-testid={"title-style-control"}>
      <FAB
        shape="square"
        radius="md"
        variant={titleStyle.fontWeight === 700 ? "primary" : "secondary"}
        aria-label="Format bold"
        size="sm"
        icon={<span className="material-icons">format_bold</span>}
        onClick={() => toggleStyle("fontWeight", 700, 400)}
      />

      <FAB
        shape="square"
        radius="md"
        variant={titleStyle.fontStyle === "italic" ? "primary" : "secondary"}
        aria-label="Format italic"
        size="sm"
        icon={<span className="material-icons">format_italic</span>}
        onClick={() => {
          onStyleChange({ ...titleStyle, fontStyle: titleStyle.fontStyle === "italic" ? "normal" : "italic" });
        }}
      />

      <FAB
        shape="square"
        radius="md"
        variant={titleStyle.textDecoration === "underline" ? "primary" : "secondary"}
        aria-label="Format underline"
        size="sm"
        icon={<span className="material-icons">format_underlined</span>}
        onClick={() => {
          onStyleChange({
            ...titleStyle,
            textDecoration: titleStyle.textDecoration === "underline" ? "none" : "underline",
          });
        }}
      />
      {titleStyle.color ? (
        <FAB
          shape="circle"
          aria-label="Reset color"
          variant="primary"
          size="sm"
          icon={<span className="material-icons">format_color_reset</span>}
          onClick={() => changeColor(null)}
        />
      ) : null}
      <CompactColorPickerInput
        value={titleStyle.color || "#000000"}
        change={(newColor) => changeColor(newColor)}
        placement="bottom-end"
        registerCloseHandler={registerCloseHandler}
      />
      <div className={styles["font-size-controls"]}>
        <NumberInput
          aria-label={"Font size"}
          defaultValue={fontSize}
          max={TITLE_MAX_SIZE}
          min={TITLE_MIN_SIZE}
          style={{ width: "120px" }}
          onChange={(value) => changeFontSize(value ?? fontSize)}
        />
      </div>
    </div>
  );
}
